<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { ROUTE_NAMES } from '@src/shared/constants/routes';

const route = useRoute();

const links = [
	{ name: ROUTE_NAMES.earn, label: 'Earn' },
	{ name: ROUTE_NAMES.portfolio, label: 'Portfolio' },
	{ name: ROUTE_NAMES.profile, label: 'Profile' },
] as const;

const activeIndex = computed(() => {
	const i = links.findIndex((link) => link.name === route.name);
	return i === -1 ? 0 : i;
});

const navEl = ref<HTMLElement | null>(null);
const thumb = shallowRef({ left: 0, width: 0 });

function syncThumb() {
	const nav = navEl.value;
	if (!nav) {
		return;
	}
	const anchors = nav.querySelectorAll<HTMLElement>('a.nav-link');
	const el = anchors[activeIndex.value];
	if (!el) {
		return;
	}
	const n = nav.getBoundingClientRect();
	const r = el.getBoundingClientRect();
	thumb.value = {
		left: r.left - n.left,
		width: r.width,
	};
}

function scheduleSync() {
	void nextTick(syncThumb);
}

let resizeObserver: null | ResizeObserver = null;

onMounted(() => {
	scheduleSync();
	resizeObserver = new ResizeObserver(scheduleSync);
	if (navEl.value) {
		resizeObserver.observe(navEl.value);
	}
	resizeObserver.observe(globalThis.document.documentElement);
});

onBeforeUnmount(() => {
	resizeObserver?.disconnect();
	resizeObserver = null;
});

watch(activeIndex, scheduleSync);
</script>

<template>
	<nav
		ref="navEl"
		class="relative hidden sm:flex w-max max-w-full bg-cardBg p-1.25 h-11 border border-cardBorder rounded-full text-primaryText"
		aria-label="Main"
	>
		<div class="thumb" aria-hidden="true" :style="{ left: `${thumb.left}px`, width: `${thumb.width}px` }" />
		<RouterLink
			v-for="(link, idx) in links"
			:key="link.name"
			:to="{ name: link.name }"
			class="nav-link relative z-10 rounded-full transition-colors duration-200"
			:class="activeIndex === idx ? 'text-primaryText' : 'text-secondaryText'"
		>
			{{ link.label }}
		</RouterLink>
	</nav>
</template>

<style scoped>
.nav-link {
	line-height: 18px;
	padding: 8px 16px;
}

.thumb {
	position: absolute;
	top: 5px;
	bottom: 5px;
	border-radius: 9999px;
	background: rgb(255 255 255 / 0.1);
	pointer-events: none;
	transition:
		left 0.2s ease,
		width 0.2s ease;
}
</style>
